import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useProjectContext } from "../contexts/ProjectContext";
import { CashFlowChart } from "../components/FlussiCassa/CashFlowChart";
import { CashFlowTable } from "../components/FlussiCassa/CashFlowTable";
import { CashPositionCard } from "../components/FlussiCassa/CashPositionCard";
import {
  calcolaCashFlow,
  formatCurrency,
  type CashFlowSummary,
} from "../services/cashFlowCalculations";
import { calcolaTaxBreakdown } from "../services/taxCalculations";
import type {
  Preventivo,
  Finanziamento,
  GanttTask,
  CostoExtra,
  Notaio as NotaioType,
} from "../types";

export function FlussiCassa() {
  const { currentProject } = useProjectContext();

  // UI states
  const [isLoading] = useState(false);
  const [error] = useState<string | null>(null);
  const [estimateLevel, setEstimateLevel] = useState<"min" | "med" | "max">(
    "med",
  );

  // Calculate cash flow (dati reali dal progetto attivo)
  const cashFlowSummary: CashFlowSummary | null = useMemo(() => {
    if (!currentProject) return null;

    const preventivi: Preventivo[] = currentProject.preventivi ?? [];
    const finanziamenti: Finanziamento[] = currentProject.finanziamenti ?? [];
    const ganttTasks: GanttTask[] = currentProject.ganttTasks ?? [];
    const costiExtra: CostoExtra[] = currentProject.costiExtra ?? [];
    const notaio: NotaioType | null = currentProject.notaio ?? null;

    const notaioData: NotaioType = notaio || {
      id: "",
      projectId: currentProject.id,
      isPrimaCasa: true,
      venditoreImpresa: false,
      percentualeAgenzia: 3,
      catastale: 0,
      conMutuo: true,
      onorarioCompravendita: 1200,
      onorarioMutuo: 650,
      visureVerifiche: 150,
      bolliDiritti: 230,
    };

    const taxBreakdown = calcolaTaxBreakdown(currentProject, notaioData);
    const dataRogito =
      currentProject.immobile?.dataRogito ||
      currentProject.dataRogito ||
      new Date().toISOString().split("T")[0];

    return calcolaCashFlow({
      project: currentProject,
      preventivi,
      finanziamenti,
      ganttTasks,
      costiExtra,
      taxBreakdown,
      estimateLevel,
      dataRogito,
    });
  }, [currentProject, estimateLevel]);

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          Seleziona un progetto per visualizzare i flussi di cassa
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Flussi di Cassa</h2>
          <p className="text-gray-600 mt-1">
            Proiezione entrate e uscite del progetto
          </p>
        </div>

        {/* Estimate level selector */}
      </div>

      {!currentProject?.immobile?.dataRogito && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-amber-800">
            Compila la data rogito nella Scheda Immobile per calcoli accurati
            dei flussi
          </p>
          <Link
            to="/immobile"
            className="ml-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
          >
            Vai alla Scheda Immobile
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div></div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Stima:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {(["min", "med", "max"] as const).map((level) => (
              <button
                key={level}
                onClick={() => setEstimateLevel(level)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  estimateLevel === level
                    ? "bg-white shadow text-primary-700"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {level === "min"
                  ? "Minima"
                  : level === "med"
                    ? "Media"
                    : "Massima"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <CashPositionCard description={currentProject.description} />
      <div>
        <h3 className="font-semibold text-lg">Proiezione dal rogito</h3>
        <p className="text-sm text-gray-600">
          Ricostruzione da finanziamenti, costi e rate registrati: il saldo
          calcolato non sostituisce la cassa attuale dichiarata.
        </p>
      </div>
      {/* Summary cards */}
      {cashFlowSummary?.events.some((event) => event.dateEstimated) && (
        <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
          Le spese senza una data di pagamento registrata hanno date indicative,
          ricavate dal calendario lavori o dal mese successivo al rogito. Questa
          proiezione non rappresenta ancora un piano di acconti e saldi:
          verifica gli impegni prima di ordinare.
        </p>
      )}
      {cashFlowSummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">+</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Entrate Totali</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(cashFlowSummary.totalEntrate)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">-</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Uscite Totali</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(cashFlowSummary.totalUscite)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  cashFlowSummary.saldoFinale >= 0
                    ? "bg-blue-100"
                    : "bg-orange-100"
                }`}
              >
                <span className="text-2xl">=</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Saldo Finale</p>
                <p
                  className={`text-2xl font-bold ${
                    cashFlowSummary.saldoFinale >= 0
                      ? "text-blue-600"
                      : "text-orange-600"
                  }`}
                >
                  {formatCurrency(cashFlowSummary.saldoFinale)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts and tables */}
      {cashFlowSummary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CashFlowChart summary={cashFlowSummary} />
          <CashFlowTable events={cashFlowSummary.events} />
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Come funziona</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Le entrate includono erogazione mutui/prestiti</li>
          <li>
            • Le uscite includono acquisto, preventivi, tasse, costi extra e
            rate
          </li>
          <li>
            • Le rate dei prestiti con data prima rata sono proiettate fino a
            fine piano
          </li>
          <li>• Cambia il livello di stima per vedere scenari diversi</li>
        </ul>
      </div>
    </div>
  );
}
